<template>
  <v-content>
    <v-container my-10>
      <v-layout align-center justify-center>
        <v-progress-circular
          :size="55"
          :width="4"
          indeterminate
          color="primary"
          class="mt-12"
          v-if="loading || getDocumentLoadingStatus || getItemLoadingStatus"
        />
        <v-form ref="form" v-model="valid" lazy-validation v-else>
          <v-card max-width="650px">
            <v-card-title>
              <span class="headline" v-if="isEditView"
                >{{ editedItem.brand_name }} {{ editedItem.model }}</span
              >
              <span class="headline" v-else>{{
                $t("builder.models.addEdit.addNew")
              }}</span>
            </v-card-title>

            <v-card-text>
              <v-container>
                <v-row dense>
                  <v-col
                    cols="12"
                    sm="12"
                    md="12"
                    lg="12"
                    v-if="canUseExternalBrand"
                  >
                    <v-select
                      :items="sourceOptions"
                      v-model="isExternalBrand"
                      item-text="text"
                      item-value="value"
                      @change="handleSourceChange"
                    ></v-select>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="12"
                    md="12"
                    lg="12"
                    v-if="!isExternalBrand"
                  >
                    <group-selector
                      v-if="!isCharter.active"
                      :crm-group-uuid="editedItem.brand_uuid"
                      @update:crm-group-uuid="changeBrand"
                      :custom-label="$t('builder.models.addEdit.brand')"
                      :rules.sync="brandRule"
                      :hide-supergroup="true"
                      data-cy="modelsBrandInput"
                      class="mt-0 pt-1"
                    />
                    <v-text-field
                      v-else
                      class="mt-0 pt-1"
                      :label="$t('builder.models.addEdit.brand')"
                      v-model="editedItem.brand_name"
                      color="primary"
                      :autocomplete="false"
                      :rules="brandRule"
                    >
                    </v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="12"
                    md="12"
                    lg="12"
                    v-if="isExternalBrand"
                  >
                    <v-text-field
                      class="mt-0 pt-1"
                      :label="$t('builder.models.addEdit.brand')"
                      v-model="editedItem.brand_name"
                      color="primary"
                      :autocomplete="false"
                      :rules="brandRule"
                    >
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="12" md="12" lg="12">
                    <v-text-field
                      v-model="editedItem.model"
                      :label="$t('builder.models.addEdit.model')"
                      color="primary"
                      required
                      :rules="modelRule"
                      data-cy="modelsModelInput"
                      :autocomplete="false"
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    sm="12"
                    md="12"
                    lg="12"
                    v-if="showIndustrialCode"
                  >
                    <v-text-field
                      color="primary"
                      :label="$t('builder.models.addEdit.industrialCode')"
                      persistent-hint
                      v-model="editedItem.industrial_code"
                      autocomplete="off"
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    sm="12"
                    md="12"
                    lg="12"
                    data-cy="modelsTypeInput"
                  >
                    <type-selector
                      :model_type_uuid="editedItem.type"
                      v-on:update:model_type_uuid="changeModelType"
                      :custom_label="$t('builder.models.addEdit.type')"
                      :rules.sync="typeRule"
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    sm="12"
                    md="12"
                    lg="12"
                    v-if="editedItem.brand_uuid"
                  >
                    <profile-selector
                      class="mb-4 mt-4"
                      :sort="false"
                      :boat_specific="false"
                      :disabled="false"
                      :selected_profile_uuid.sync="
                        editedItem.selected_profile_uuid
                      "
                      :selected_brand_uuid="editedItem.brand_uuid"
                      :initial_data="editedItem.selected_profile"
                      :key="profileSelectorKey"
                    />
                  </v-col>

                  <v-col cols="12" sm="12" md="12" lg="12">
                    <direct-file-upload
                      class="mb-4"
                      :multiple="false"
                      :files="
                        isEditView
                          ? [editedItem.mobile_image_file_resource]
                          : []
                      "
                      @saved="handleDirectUploadFiles"
                      :loading.sync="loading"
                      :hint="
                        $t(
                          'builder.models.addEdit.uploadAMobileAppBackgroundImageForLiteNonConnectedBoatsEnsureTheImageIsAtLeast2000pxTallItWillBeCenterCroppedWhenDisplayedOnPhonesSoAimToHaveTheSubjectHorizontallyCenteredForTheBestResult'
                        )
                      "
                      persistent-hint
                      asset-type="image_asset"
                      ref="directFileUpload"
                      :label="
                        $t('builder.models.addEdit.mobileBackgroundImage')
                      "
                    >
                      <template v-slot:default="{ file }">
                        <v-menu v-model="menu" offset-x>
                          <template v-slot:activator="{ on, attrs }">
                            <div
                              style="cursor: pointer; position: absolute"
                              v-bind="attrs"
                              v-on="on"
                              @mouseover="menu = true"
                              @mouseleave="menu = false"
                            >
                              <v-icon class="mr-2">mdi-file-image</v-icon>
                              {{ file.name }}
                            </div>
                          </template>

                          <v-card class="pa-2">
                            <v-list>
                              <v-list-item>
                                <v-list-item-content>
                                  <v-img
                                    max-height="150"
                                    max-width="250"
                                    :src="
                                      typeof file.src === 'undefined'
                                        ? getResizedImageSrc(file.link)
                                        : file.src
                                    "
                                  ></v-img>
                                </v-list-item-content>
                              </v-list-item>
                            </v-list>
                          </v-card>
                        </v-menu>
                      </template>
                    </direct-file-upload>
                  </v-col>

                  <v-col cols="12" sm="12" md="12" lg="12">
                    <v-textarea
                      v-model="editedItem.comment"
                      :label="$t('builder.models.addEdit.description')"
                      color="primary"
                      auto-grow
                      data-cy="modelsDescriptionInput"
                    />
                  </v-col>

                  <!-- ITEMS -->
                  <v-col cols="12" sm="12" md="12" lg="12" class="mb-0 pb-0">
                    <v-row no-gutters>
                      <h3 class="subtitle-1 mt-1 mb-2">
                        {{ $t("builder.models.addEdit.parts") }}
                      </h3>
                      <v-spacer></v-spacer>
                      <licence-tooltip
                        :feature="createNewPartsHandler"
                        :disabled="createNewPartsHandler.disabled"
                        :enabled="createNewPartsHandler.enabled"
                        float-direction="right"
                      >
                        <template v-slot:action="{ attrs, disabled, enabled }">
                          <v-btn
                            class="float-right"
                            color="primary"
                            text
                            @click="openItemDialog()"
                            data-cy="modelsItemDialogBtn"
                            v-bind="attrs"
                            :disabled="disabled"
                          >
                            <v-icon small class="mr-1" v-if="enabled">
                              mdi-star-outline </v-icon
                            >{{
                              $t("builder.models.addEdit.createPart")
                            }}</v-btn
                          >
                        </template>
                      </licence-tooltip>
                    </v-row>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="12"
                    md="12"
                    lg="12"
                    data-cy="modelsItemInput"
                    class="mt-0 pt-0 mb-4"
                  >
                    <item-picker
                      :items="editedItem.items"
                      :preview="false"
                      :readonly="false"
                      @add:item="addNewItem($event)"
                      @delete:item="deleteItem($event)"
                      feature="add-part-on-model"
                    />
                  </v-col>

                  <!-- DOCUMENTS -->
                  <v-col cols="12" sm="12" md="12" lg="12" class="mb-0 pb-0">
                    <v-row no-gutters>
                      <h3 class="subtitle-1 mt-1 mb-2">
                        {{ $t("builder.models.addEdit.documents") }}
                      </h3>
                      <v-spacer></v-spacer>
                      <licence-tooltip
                        :feature="createNewDocumentsHandler"
                        :disabled="createNewDocumentsHandler.disabled"
                        :enabled="createNewDocumentsHandler.enabled"
                        float-direction="right"
                      >
                        <template v-slot:action="{ attrs, disabled, enabled }">
                          <v-btn
                            color="primary"
                            text
                            @click="openDocumentDialog()"
                            data-cy="modelsDocumentDialogBtn"
                            v-bind="attrs"
                            :disabled="disabled"
                          >
                            <v-icon small class="mr-1" v-if="enabled">
                              mdi-star-outline </v-icon
                            >{{
                              $t("builder.models.addEdit.createDocument")
                            }}</v-btn
                          >
                        </template>
                      </licence-tooltip>
                    </v-row>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="12"
                    md="12"
                    lg="12"
                    data-cy="modelsDocumentInput"
                  >
                    <document-picker
                      :documents.sync="editedItem.documents"
                      :unavailable-documents="itemsDocumentsSelected"
                      @add:document="addNewDocument($event, false)"
                      @delete:document="deleteDocument($event, false)"
                      feature="add-document-on-model"
                    />
                    <!-- v-on:delete:document=" editedItem.documents.splice($event,
                    1) " -->
                  </v-col>
                  <v-col cols="12" sm="12" md="12" lg="12">
                    <v-card-actions class="mt-4 pt-0 pl-0 ml-0">
                      <!-- <v-spacer></v-spacer> -->
                      <v-btn
                        text
                        class="mt-0 pt-0 pl-0 ml-0"
                        color="primary"
                        @click="showAdvanceSection = !showAdvanceSection"
                      >
                        <v-icon>{{
                          showAdvanceSection
                            ? "mdi-chevron-up"
                            : "mdi-chevron-down"
                        }}</v-icon>
                        {{ $t("builder.models.addEdit.advanced") }}
                      </v-btn>
                    </v-card-actions>
                    <v-expand-transition class="ml-0 pl-0">
                      <div v-show="showAdvanceSection">
                        <v-card-title class="subtitle-1 mt-4 pt-0 pl-0 ml-0">
                          {{ $t("builder.models.addEdit.installationOptions") }}
                        </v-card-title>
                        <v-card-text class="mt-0 pt-0 pl-0 ml-0"
                          >{{
                            $t(
                              "builder.models.addEdit.theFollowingOptionsApplyOnlyToInFactoryInstallations"
                            )
                          }}
                        </v-card-text>
                        <v-card-text class="mt-0 pt-0 pl-0 ml-0">
                          <v-checkbox
                            class="mt-2"
                            color="primary"
                            v-model="editedItem.diag_skip_fimrware_update"
                            :label="
                              $t('builder.models.addEdit.skipFirmwareUpdate')
                            "
                          ></v-checkbox>
                          <v-checkbox
                            class="mt-0"
                            color="primary"
                            v-model="editedItem.diag_skip_nmea"
                            :label="
                              $t('builder.models.addEdit.skipNMEANetworkScan')
                            "
                          ></v-checkbox>

                          <v-card-text class="mt-0 pt-0 pb-2 pl-1 ml-0">
                            {{
                              $t(
                                "builder.models.addEdit.onlyInProductionModelsAreAvailableToFactoryInstallers"
                              )
                            }}
                          </v-card-text>

                          <v-checkbox
                            class="mt-0"
                            color="primary"
                            v-model="editedItem.is_in_production"
                            :label="$t('builder.models.addEdit.inProduction')"
                          ></v-checkbox>
                        </v-card-text>
                      </div>
                    </v-expand-transition>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="primary"
                data-cy="modelsCancelBtn"
                text
                @click="close()"
                >{{ $t("builder.models.addEdit.cancel") }}</v-btn
              >
              <v-btn
                color="primary"
                data-cy="modelsSaveBtn"
                text
                @click="save"
                >{{ $t("builder.models.addEdit.save") }}</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-form>
      </v-layout>
      <inline-item-dialog></inline-item-dialog>
      <inline-document-dialog></inline-document-dialog>
      <warning-dialog
        :dialog="warningDialog"
        :item="dirtyItem.name"
        :text="warningText"
        :title="warningTitle"
        @agree="newItemDialog"
        v-show="false"
      />
      <unsaved-content-warning
        :dialog="unsavedContent"
        @redirect="redirectRoute"
      ></unsaved-content-warning>
    </v-container>
  </v-content>
</template>
<script>
import api from "@/utils/api.js";
import ItemPicker from "@/components/pickers/ItemPicker";
import DocumentPicker from "@/components/pickers/DocumentPicker";
import GroupSelector from "@/components/selectors/GroupSelector";
import TypeSelector from "@/components/selectors/ModelTypeSelector";
import WarningDialog from "@/components/dialogs/WarningDialog.vue";
import ProfileSelector from "@/components/selectors/ProfileSelector";
import snackbar from "@/utils/snackbar.js";
import InlineItemDialog from "@/components/dialogs/InlineItemDialog";
import InlineDocumentDialog from "@/components/dialogs/InlineDocumentDialog";
import UnsavedContentWarning from "@/components/dialogs/UnsavedContentWarningDialog";
import restrictions from "@/utils/restrictions";
import { mapGetters } from "vuex";
import LicenceTooltip from "@/components/general/LicenceTooltip";
import DirectFileUpload from "@/components/miscellaneous/DirectFileUpload.vue";

export default {
  components: {
    ProfileSelector,
    GroupSelector,
    ItemPicker,
    DocumentPicker,
    WarningDialog,
    TypeSelector,
    InlineItemDialog,
    InlineDocumentDialog,
    UnsavedContentWarning,
    LicenceTooltip,
    DirectFileUpload,
  },
  data: () => ({
    sourceOptions: [],
    isModelEdited: false,
    valid: true,
    warningDialog: false,
    warningText: "",
    warningTitle: "",
    brandRule: [],
    modelRule: [],
    typeRule: [],
    loading: true,
    isModelDirty: false,
    documentSearch: "",
    documentOptions: [],
    dirtyItem: { name: "" },
    dirtyItemIndex: null,
    addRemoveContent: { type: "ITEM", add: true },
    isEditView: null,
    isCharter: { active: false, group: {} },
    groupOptions: [],
    itemsDocumentsSelected: [],
    profileSelectorKey: 0,
    unsavedContent: { active: false, route: null },
    showAdvanceSection: false,
    isExternalBrand: false,
    menu: false,
    editedItem: {
      is_external_brand: false,
      is_in_production: true,
      brand_name: null,
      brand_uuid: null,
      model: "",
      type: "",
      comment: "",
      documents: [],
      items: [],
      crm_group: null,
    },
  }),

  computed: {
    ...mapGetters([
      "getDocumentLoadingStatus",
      "getItemLoadingStatus",
      "getMe",
      "createNewDocumentsHandler",
      "createNewPartsHandler",
    ]),
    showIndustrialCode() {
      return !!restrictions.canUseCrmFeature("has_industrial_codes");
    },
    canUseExternalBrand() {
      return !!restrictions.canUseCrmFeature("can_oem_manage_non_group_boats");
    },
  },
  created() {
    this.setInitialData();
    this.getIsCharter();
    if (Object.keys(this.isCharter.group).length !== 0) {
      this.isCharter.active = true;
    }
    if (this.$route.name === "modelsEdit") {
      this.isEditView = true;
      api.getModel(this.$route.params.id).then((resp) => {
        this.editedItem = resp.data;

        this.isExternalBrand = this.editedItem.brand.is_external_brand;

        this.editedItem.crm_group_uuid = this.editedItem.crm_group
          ? this.editedItem.crm_group.uuid
          : null;

        this.editedItem.brand_uuid = this.editedItem.brand
          ? this.editedItem.brand.uuid
          : null;

        this.editedItem.mobile_image_file_resource_uuid = this.editedItem
          .mobile_image_file_resource
          ? this.editedItem.mobile_image_file_resource.uuid
          : null;

        this.editedItem.document_uuids = [];
        this.editedItem.documents.forEach((document) => {
          this.editedItem.document_uuids.push(document.uuid);
          // this.itemsDocumentsSelected.push(document.uuid);
          this.documentOptions.push(document);
        });
        this.editedItem.items.forEach((item) => {
          item.documents.forEach((document) => {
            this.itemsDocumentsSelected.push(document.uuid);
          });
        });
        this.loading = false;
      });
    } else {
      this.loading = false;
    }
  },

  mounted() {
    this.$store.subscribe((mutation, state) => {
      if (
        mutation.type === "ITEMADDED" &&
        (state.item.from === "modelsEdit" || state.item.from === "modelsNew")
      ) {
        this.addNewItem(mutation.payload.item);
      }
      if (
        mutation.type === "DOCUMENTADDED" &&
        (state.document.from === "modelsEdit" ||
          state.document.from === "modelsNew")
      ) {
        this.addNewDocument(mutation.payload.document);
      }
    });

    if (this.$route.name !== "modelsEdit") {
      this.$watch("editedItem", this.formEdited, {
        deep: true,
      });
    }
  },

  methods: {
    setInitialData() {
      this.brandRule = [
        (v) => !!v || this.$t("builder.models.addEdit.brandIsRequired"),
      ];
      this.modelRule = [
        (v) => !!v || this.$t("builder.models.addEdit.modelIsRequired"),
      ];
      this.typeRule = [
        (v) => !!v || this.$t("builder.models.addEdit.typeIsRequired"),
      ];
      this.sourceOptions = [
        {
          text: this.$t("builder.models.addEdit.organization"),
          value: false,
        },
        {
          text: this.$t("builder.models.addEdit.nonOrganizationBoat"),
          value: true,
        },
      ];
    },

    handleDirectUploadFiles(uuid) {
      const { fileUuids } = uuid;
      this.editedItem.mobile_image_file_resource_uuid = fileUuids[0];
      this.isEditView ? this.update() : this.create();
    },

    getResizedImageSrc(src) {
      if (!src) {
        return "";
      }

      const extansion =
        src.substring(src.lastIndexOf(".") + 1, src.length) || src;

      return `${src.split(extansion)[0]}250x150.${extansion}`;
    },
    handleSourceChange(event) {
      if (event) {
        api.groupsAutocomplete({}).then((resp) => {
          const externalBrand = resp.data.find(
            (item) => item.is_external_brand
          );
          if (typeof externalBrand === "undefined") {
            return;
          }
          this.editedItem.brand_uuid = externalBrand.uuid;
          this.profileSelectorKey++;
        });
      }
    },
    getIsCharter() {
      let group = this.getMe.crm_groups.find(
        (group) =>
          group.is_supergroup &&
          group.features.find(
            (feature) =>
              feature.name === "has_shallow_models" &&
              feature.value.toLowerCase() === "true"
          )
      );
      if (typeof group !== "undefined") {
        this.isCharter.group = group;
      }
    },
    formEdited: function (newV, oldV) {
      this.isModelEdited = true;
    },

    // getDialogText() {
    //   var array = this.warningTitle.split(" ");
    //   array.splice(array.indexOf("item"), 1, this.dirtyItem.name);
    //   this.warningTitle = array.join(" ");
    // },

    redirectRoute(event) {
      if (event) {
        this.$router.push(this.unsavedContent.route);
      }
      this.unsavedContent.active = false;
      this.unsavedContent.route = null;
    },

    addNewDocument(data, directAdd = false) {
      if (directAdd) {
        this.editedItem.documents.push(data);
        return;
      }

      if (this.isEditView && this.editedItem.has_boats) {
        this.addRemoveContent = { type: "DOCUMENT", add: true };
        this.warningText = this.$t(
          "builder.models.addEdit.changesForThisDocumentToExistingBoatsNeedToBeDoneManuallyOnEachBoat"
        );
        this.warningTitle = this.$t(
          "builder.models.addEdit.thisDocumentWillOnlyBeAppliedRemovedOnNewBoatsOfThisModel"
        );
        this.warningDialog = true;
        this.dirtyItem = data;
      } else {
        this.editedItem.documents.push(data);
      }
    },

    deleteDocument(dirtyItemIndex, directRemove = false) {
      if (directRemove) {
        this.editedItem.documents.splice(dirtyItemIndex, 1);
        return;
      }

      if (this.isEditView && this.editedItem.has_boats) {
        this.addRemoveContent = { type: "DOCUMENT", add: false };
        this.warningText = this.$t(
          "builder.models.addEdit.changesForThisItemToExistingBoatsNeedToBeDoneManuallyOnEachBoat"
        );
        this.warningTitle = this.$t(
          "builder.models.addEdit.thisItemWillOnlyBeAppliedRemovedOnNewBoatsOfThisModel"
        );
        this.warningDialog = true;
        this.dirtyItem = this.editedItem.documents[dirtyItemIndex];
        this.dirtyItemIndex = dirtyItemIndex;
      } else {
        this.editedItem.documents.splice(dirtyItemIndex, 1);
      }
    },

    openDocumentDialog() {
      this.$store.commit("DOCUMENTPATH", this.$route.name);
      this.$store.commit("DOCUMENTDIALOGSTATUS", true);
    },

    openItemDialog() {
      this.$store.commit("ITEMPATH", this.$route.name);
      this.$store.commit("ITEMDIALOGSTATUS", true);
    },
    changeBrand(value) {
      this.editedItem.brand_uuid = value;
      this.profileSelectorKey++;
    },
    changeModelType(value) {
      this.editedItem.type = value;
    },

    addNewItem(item) {
      if (this.isEditView && this.editedItem.has_boats) {
        this.addRemoveContent = { type: "ITEM", add: true };
        this.warningText = this.$t(
          "builder.models.addEdit.changesForThisItemToExistingBoatsNeedToBeDoneManuallyOnEachBoat"
        );
        this.warningTitle = this.$t(
          "builder.models.addEdit.thisItemWillOnlyBeAppliedRemovedOnNewBoatsOfThisModel"
        );
        this.warningDialog = true;
        this.dirtyItem = item;
      } else {
        this.addItem(item);
      }
    },

    newItemDialog(agreed) {
      if (
        agreed &&
        this.addRemoveContent.type === "ITEM" &&
        this.addRemoveContent.add
      ) {
        this.addItem(this.dirtyItem);
        this.addRemoveContent = {};
      } else if (
        agreed &&
        this.addRemoveContent.type === "ITEM" &&
        !this.addRemoveContent.add
      ) {
        this.removeItem(this.dirtyItemIndex);
        this.addRemoveContent = {};
      }

      if (
        agreed &&
        this.addRemoveContent.type === "DOCUMENT" &&
        this.addRemoveContent.add
      ) {
        this.addNewDocument(this.dirtyItem, true);
        this.addRemoveContent = {};
      } else if (
        agreed &&
        this.addRemoveContent.type === "DOCUMENT" &&
        !this.addRemoveContent.add
      ) {
        this.deleteDocument(this.dirtyItemIndex, true);
        this.addRemoveContent = {};
      }

      this.warningDialog = false;
    },

    addItem(item) {
      this.editedItem.items.push(item);
      this.itemsDocumentsSelected = item.documents.map(
        (element) => element.uuid
      );
      this.itemsDocumentsSelected = [...new Set(this.itemsDocumentsSelected)];
    },

    removeItem(item) {
      let documentsUuid = [];
      this.editedItem.items.forEach((element) => {
        documentsUuid = [...element.documents.map((document) => document.uuid)];
      });

      documentsUuid.forEach((element) => {
        let index = this.itemsDocumentsSelected.indexOf(element.uuid);
        if (index !== -1) {
          this.itemsDocumentsSelected.splice(index, 1);
        }
      });

      this.editedItem.items.splice(item, 1);
    },

    deleteItem(dirtyItemIndex) {
      if (this.isEditView && this.editedItem.has_boats) {
        this.addRemoveContent = { type: "ITEM", add: false };
        this.warningText = this.$t(
          "builder.models.addEdit.changesForThisItemToExistingBoatsNeedToBeDoneManuallyOnEachBoat"
        );
        this.warningTitle = this.$t(
          "builder.models.addEdit.thisItemWillOnlyBeAppliedRemovedOnNewBoatsOfThisModel"
        );
        this.warningDialog = true;
        this.dirtyItem = this.editedItem.items[dirtyItemIndex];
        this.dirtyItemIndex = dirtyItemIndex;
      } else {
        this.removeItem(dirtyItemIndex);
      }
    },

    close(uuid) {
      if (typeof uuid !== "undefined") {
        this.$router.push({
          name: "modelsDetails",
          params: { id: uuid },
        });
      } else {
        this.$router.push({
          name: "models",
        });
      }
    },

    handleError(err) {
      // if (err.status === 403) {
      //   snackbar.error("Don't have permission to edit models ");
      // } else {
      //   snackbar.error("An error has occurred");
      // }
      this.loading = false;
    },

    create() {
      api
        .createModel(this.editedItem)
        .then((resp) => {
          //this.loading = false;
          snackbar.success(this.$t("utils.snackbar.success.successfullySaved"));
          this.isModelEdited = false;
          this.close(resp.data.uuid);
        })
        .catch((err) => {
          this.handleError(err.response);
        });
    },
    update() {
      api
        .updateModel(this.editedItem.uuid, this.editedItem)
        .then((resp) => {
          //this.loading = false;
          snackbar.success(this.$t("utils.snackbar.success.successfullySaved"));
          this.isModelEdited = false;
          this.close(resp.data.uuid);
        })
        .catch((err) => {
          this.handleError(err.response);
        });
    },
    save() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        // Before saving fill in all item UUIDS
        this.editedItem.item_uuids = this.editedItem.items.map(
          (item) => item.uuid
        );
        // Group uuid is implicit
        if (this.isCharter.active) {
          this.editedItem.brand_uuid = this.isCharter.group.uuid;
        } else {
          this.editedItem.crm_group_uuid = this.editedItem.brand_uuid;
          if (!this.isExternalBrand) {
            this.editedItem.is_external_brand = false;
            delete this.editedItem.brand;
            delete this.editedItem.brand_name;
          } else {
            this.editedItem.is_external_brand = true;
            delete this.editedItem.brand_uuid;
          }
        }

        this.editedItem.document_uuids = this.editedItem.documents.map(
          (document) => document.uuid
        );
        if (this.$refs.directFileUpload.dirty) {
          //Order of code below is important!!
          this.$refs.directFileUpload.save().then((resp) => {
            this.handleDirectUploadFiles(resp);
          });
          this.loading = true;
        } else {
          if (this.$refs.directFileUpload.selectedFiles.length === 0) {
            this.editedItem.mobile_image_file_resource_uuid = null;
          }
          this.loading = true;
          this.isEditView ? this.update() : this.create();
        }
      } else {
        snackbar.error();
      }
    },
  },
  beforeRouteLeave(to, from, next) {
    if (!this.isModelEdited) {
      next();
    } else {
      if (this.unsavedContent.route) {
        next();
      } else {
        this.unsavedContent.active = true;
        this.unsavedContent.route = to;
        next(false);
      }
    }
  },
};
</script>
